/**
 * mobile-flip plugin
 *
 * When a section carries `data-mobile-flip="<delay-ms>"`, the active slide
 * receives the class `custom-swiper-slide-active` after the given delay
 * following each slide change. Used to trigger CSS flip animations only
 * after the swipe settles.
 *
 * Reference plugin — keeps state in a WeakMap keyed by swiper, never on the
 * instance itself, so multiple plugins can't collide on instance properties.
 */

const ACTIVE_CLASS = 'custom-swiper-slide-active';
const DEFAULT_DELAY = 1000;

const timersBySwiper = new WeakMap();
const handlersBySwiper = new WeakMap();

export const mobileFlip = {
  name: 'mobile-flip',

  matches(_el, section) {
    // Activate only when an explicit non-empty value is set. Previously this
    // used `!= null`, which also activates on `data-mobile-flip=""` — a
    // shape Liquid will produce when rendering an unset variable, e.g.
    // `data-mobile-flip="{{ section.settings.flip_delay }}"` with the
    // setting cleared. Empty string would silently enable the plugin at
    // DEFAULT_DELAY=1000ms and surprise the merchant.
    const value = section?.dataset?.mobileFlip;
    return typeof value === 'string' && value.length > 0;
  },

  afterInit(swiper, ctx) {
    const delayAttr = ctx.parentSwiperSection.dataset.mobileFlip;
    const parsed = parseInt(delayAttr, 10);
    const delay = Number.isNaN(parsed) ? DEFAULT_DELAY : parsed;

    const handler = () => {
      swiper.slides.forEach((slide) => slide.classList.remove(ACTIVE_CLASS));

      const previous = timersBySwiper.get(swiper);
      if (previous) clearTimeout(previous);

      const timer = setTimeout(() => {
        const active = swiper.slides[swiper.activeIndex];
        if (active) active.classList.add(ACTIVE_CLASS);
      }, delay);

      timersBySwiper.set(swiper, timer);
    };

    swiper.on('slideChange', handler);
    handlersBySwiper.set(swiper, handler);
  },

  onDestroy(swiper) {
    const timer = timersBySwiper.get(swiper);
    if (timer) clearTimeout(timer);
    timersBySwiper.delete(swiper);

    // Swiper.destroy() already detaches its own listeners, but clearing our
    // handler reference helps GC if anything still holds the swiper.
    handlersBySwiper.delete(swiper);
  },
};

export default mobileFlip;
